import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { BillDetailTile } from '@/components/BillDetailTile';
import { useDeliveryStore } from '@/stores/deliveryStore';
import { logQueueService } from '@/services/logQueue';
import { gpsService } from '@/services/gps';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { api } from '@/api/endpoints';
import { StorageKeys } from '@/lib/config';
import { buildLog, CaptureFields, type Assignment } from '@/types/domain';

export function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const loadStop = useDeliveryStore((s) => s.loadStop);
  const startEnRoute = useDeliveryStore((s) => s.startEnRoute);
  const stop = useDeliveryStore((s) => s.stop);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<Assignment | null>(null);
  const [removing, setRemoving] = useState(false);

  const driverNumber =
    typeof localStorage !== 'undefined'
      ? (localStorage.getItem(StorageKeys.driverNumber) ?? 'driver')
      : 'driver';

  useEffect(() => {
    if (id) void loadStop(id);
    // Warm the GPS watch now so a fix is cached before EN ROUTE is tapped.
    gpsService.primeFromGesture();
  }, [id, loadStop]);

  if (!stop) {
    return (
      <AppShell title="Delivery" showBack>
        <p className="text-brand-muted">Loading...</p>
      </AppShell>
    );
  }

  const first = stop.bills[0];
  const address = [stop.address, first?.remConsigneeSuite].filter(Boolean).join(' ');

  const accept = async () => {
    setBusy(true);
    try {
      gpsService.primeFromGesture();
      await startEnRoute();
      navigate(`/unload/${id}/progress`);
    } finally {
      setBusy(false);
    }
  };

  const removeBill = async (bill: Assignment) => {
    setRemoving(true);
    try {
      const fix = gpsService.peekFix();
      await logQueueService.enqueue({
        logType: buildLog.removedFromDeliveryList(driverNumber),
        display: 'Display',
        assignmentId: bill.remF04Id,
        tag: bill.remF02Tag ?? undefined,
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
        additionalFields: { [CaptureFields.switch99]: '99' },
      });
      await logQueueService.flush();
      try {
        await api.ackComplete(bill.remF04Id);
      } catch {
        // Log carries log_L08_f31_switch=99; ACK PATCH is belt-and-suspenders.
      }
      await assignmentRepo.markComplete(bill.remF04Id);
      setConfirmRemove(null);
      const remaining = (stop?.bills ?? []).filter((b) => b.remF04Id !== bill.remF04Id);
      if (remaining.length === 0) {
        navigate('/unload', { replace: true });
      } else if (id) {
        await loadStop(id);
      }
    } finally {
      setRemoving(false);
    }
  };

  return (
    <AppShell title={stop.name || 'Consignee'} showBack>
      <Card accent="delivery">
        <CardBody>
          <div className="text-lg font-bold">{stop.name || '--'}</div>
          <div className="text-sm text-brand-muted">
            {address || '--'}
            {stop.city ? `, ${stop.city}` : ''}
          </div>
        </CardBody>
      </Card>

      <ul className="mt-3 space-y-2">
        {stop.bills.map((b) => (
          <li key={b.remF04Id} className="space-y-2">
            <BillDetailTile assignment={b} kind="delivery" defaultOpen={stop.bills.length === 1} />
            <Button
              variant="outline"
              size="lg"
              fullWidth
              className="border-brand-error text-brand-error"
              onClick={() => setConfirmRemove(b)}
            >
              REMOVE DEL {b.remF02Tag ?? b.remF04Id}
            </Button>
          </li>
        ))}
      </ul>

      <div className="mt-4">
        <Button variant="delivery" size="xl" fullWidth disabled={busy} onClick={accept}>
          {busy ? 'Starting...' : 'EN ROUTE'}
        </Button>
      </div>

      <Dialog
        open={!!confirmRemove}
        onClose={() => !removing && setConfirmRemove(null)}
        title="Remove delivery?"
      >
        <p className="text-sm text-brand-muted">
          Remove PRO {confirmRemove?.remF02Tag ?? confirmRemove?.remF04Id} from your delivery list?
          Dispatch will see this freight back on dock.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" disabled={removing} onClick={() => setConfirmRemove(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={removing || !confirmRemove}
            onClick={() => confirmRemove && void removeBill(confirmRemove)}
          >
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </div>
      </Dialog>
    </AppShell>
  );
}
